import { useState, useEffect, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useCalendarView } from '../useCalendarView';
import { getViewportDimensions, isPortrait } from '../../../utils/calendar/mobile/mobileHelpers';

/**
 * Mobile-specific calendar view hook that extends the base calendar view
 */
export const useMobileCalendarView = (workingHours = [], weekStart = null) => {
    // Extend base calendar view functionality
    const baseView = useCalendarView(workingHours, weekStart);

    // Mobile-specific state
    const [selectedDay, setSelectedDay] = useState(() => {
        const today = dayjs();
        const weekStartDate = weekStart || baseView.weekStartDate;
        const weekEndDate = dayjs(weekStartDate).add(6, 'day');

        // If today is in the current week, select it, otherwise select first day of week
        if (today.isBetween(weekStartDate, weekEndDate, 'day', '[]')) {
            return today;
        }
        return weekStartDate;
    });

    const [viewMode, setViewMode] = useState('day'); // 'day' | 'week' | 'agenda'
    const [isLandscape, setIsLandscape] = useState(!isPortrait());
    const [touchMode, setTouchMode] = useState('scroll'); // 'scroll' | 'select' | 'zoom'
    const [zoomLevel, setZoomLevel] = useState(1);

    // Viewport dimensions
    const [viewport, setViewport] = useState(getViewportDimensions());

    // Update orientation and viewport on resize
    useEffect(() => {
        const handleResize = () => {
            setIsLandscape(!isPortrait());
            setViewport(getViewportDimensions());
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    // Auto-switch view mode based on orientation
    useEffect(() => {
        if (isLandscape && viewMode === 'day') {
            setViewMode('week');
        } else if (!isLandscape && viewMode === 'week') {
            setViewMode('day');
        }
    }, [isLandscape, viewMode]);

    // Calculate visible time range for the selected day
    const dayTimeRange = useMemo(() => {
        if (!selectedDay || workingHours.length === 0) {
            return { start: null, end: null, hours: [] };
        }

        const dayOfWeekMapping = {
            1: 'MONDAY',
            2: 'TUESDAY',
            3: 'WEDNESDAY',
            4: 'THURSDAY',
            5: 'FRIDAY',
            6: 'SATURDAY',
            7: 'SUNDAY'
        };
        const dayOfWeek = dayOfWeekMapping[selectedDay.isoWeekday()];
        const dayHours = workingHours.find(wh => wh.dayOfWeek === dayOfWeek);

        if (!dayHours) {
            return { start: null, end: null, hours: [] };
        }

        const [startHour, startMinute] = dayHours.openingTime.split(':').map(Number);
        const [endHour, endMinute] = dayHours.closingTime.split(':').map(Number);

        const start = selectedDay.hour(startHour).minute(startMinute).second(0);
        const end = selectedDay.hour(endHour).minute(endMinute).second(0);

        // Generate hourly slots
        const hours = [];
        let current = start.clone();
        while (current.isBefore(end) || current.isSame(end)) {
            hours.push(current.clone());
            current = current.add(1, 'hour');
        }

        return { start, end, hours };
    }, [selectedDay, workingHours]);

    // Navigate to previous day
    const goToPreviousDay = useCallback(() => {
        setSelectedDay(prev => prev.subtract(1, 'day'));
    }, []);

    // Navigate to next day
    const goToNextDay = useCallback(() => {
        setSelectedDay(prev => prev.add(1, 'day'));
    }, []);

    // Navigate to specific day
    const goToDay = useCallback((date) => {
        setSelectedDay(dayjs(date));
    }, []);

    // Navigate to today
    const goToToday = useCallback(() => {
        const today = dayjs();
        setSelectedDay(today);

        // Also update week if needed
        const currentWeekStart = baseView.weekStartDate;
        if (!today.isBetween(currentWeekStart, currentWeekStart.add(6, 'day'), 'day', '[]')) {
            baseView.goToDate(today);
        }
    }, [baseView]);

    // Check if a day has events
    const dayHasEvents = useCallback((date, events) => {
        const dayDate = dayjs(date);
        return events.some(event => {
            const eventStart = dayjs(event.startTime);
            return eventStart.isSame(dayDate, 'day');
        });
    }, []);

    // Get events for selected day
    const getSelectedDayEvents = useCallback((events) => {
        return events.filter(event => {
            const eventStart = dayjs(event.startTime);
            return eventStart.isSame(selectedDay, 'day');
        }).sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime)));
    }, [selectedDay]);

    // Calculate optimal hour height based on viewport
    const getMobileHourHeight = useCallback(() => {
        const baseHeight = 80; // Base height for mobile
        const scaledHeight = baseHeight * zoomLevel;

        // Adjust based on viewport height
        const minHeight = 60;
        const maxHeight = 120;

        return Math.max(minHeight, Math.min(maxHeight, scaledHeight));
    }, [zoomLevel]);

    // Get visible hours for current viewport
    const getVisibleHours = useCallback(() => {
        if (!dayTimeRange.start || !dayTimeRange.end) return [];

        const hourHeight = getMobileHourHeight();
        const viewportHeight = viewport.safeHeight - 200; // Account for headers
        const hoursVisible = Math.ceil(viewportHeight / hourHeight);

        // Center around current time if today
        if (selectedDay.isSame(dayjs(), 'day')) {
            const now = dayjs();
            const currentHour = now.hour();
            const startHour = Math.max(
                dayTimeRange.start.hour(),
                currentHour - Math.floor(hoursVisible / 2)
            );

            return dayTimeRange.hours.filter(hour => {
                const h = hour.hour();
                return h >= startHour && h < startHour + hoursVisible;
            });
        }

        return dayTimeRange.hours.slice(0, hoursVisible);
    }, [dayTimeRange, viewport, getMobileHourHeight, selectedDay]);

    // Touch mode handlers
    const enableSelectMode = useCallback(() => setTouchMode('select'), []);
    const enableScrollMode = useCallback(() => setTouchMode('scroll'), []);
    const enableZoomMode = useCallback(() => setTouchMode('zoom'), []);

    // Zoom handlers
    const zoomIn = useCallback(() => {
        setZoomLevel(prev => Math.min(2, prev + 0.25));
    }, []);

    const zoomOut = useCallback(() => {
        setZoomLevel(prev => Math.max(0.5, prev - 0.25));
    }, []);

    const resetZoom = useCallback(() => {
        setZoomLevel(1);
    }, []);

    return {
        // All base view properties
        ...baseView,

        // Mobile-specific properties
        selectedDay,
        viewMode,
        isLandscape,
        touchMode,
        zoomLevel,
        viewport,
        dayTimeRange,

        // Mobile-specific methods
        setSelectedDay,
        setViewMode,
        setTouchMode,
        goToPreviousDay,
        goToNextDay,
        goToDay,
        goToToday,
        dayHasEvents,
        getSelectedDayEvents,
        getMobileHourHeight,
        getVisibleHours,
        enableSelectMode,
        enableScrollMode,
        enableZoomMode,
        zoomIn,
        zoomOut,
        resetZoom,
    };
};